import type { TFunction } from 'i18next'
import type { AnalysisSnapshotOut, ReferenceContentDetail, ReferenceContentPlatform, ReferenceContentType } from '../api'
import {
  buildInsightSections,
  buildMediaOverviewFacts,
  isMediaLayerVisible,
  type MediaOverviewFacts,
} from './competitiveContentRealData'

const TENANT_ERROR_CODES = new Set([
  'CAPTURE_FAILED',
  'CAPTURE_URL_UNREACHABLE',
  'MEDIA_ANALYSIS_FAILED',
  'AI_ATTACHMENT_INVALID',
  'AI_WORKER_UNAVAILABLE',
  'AI_TIMEOUT',
  'MEDIA_DOWNLOAD_FAILED',
  'AI_ANALYSIS_FAILED',
  'GENERIC_ANALYSIS_FAILED',
])

export function tenantErrorMessageI18n(errorCode: string | null | undefined, t: TFunction): string | null {
  if (!errorCode) return null
  const normalized = errorCode.trim().toUpperCase()
  if (!TENANT_ERROR_CODES.has(normalized)) return null
  const localized = t(`competitiveAnalysis:errors.${normalized}`, { defaultValue: '' })
  return localized || null
}

export function referenceStatusLabel(status: string, t: TFunction): string {
  const localized = t(`competitiveAnalysis:status.${status}`, { defaultValue: '' })
  if (localized) return localized
  return t(`status:${status}`, { defaultValue: status })
}

export function platformLabel(platform: ReferenceContentPlatform, t: TFunction): string {
  return t(`competitiveAnalysis:platform.${platform}`, { defaultValue: platform })
}

export function contentTypeLabel(contentType: ReferenceContentType, t: TFunction): string {
  return t(`competitiveAnalysis:contentType.${contentType}`, { defaultValue: contentType })
}

export function componentStatusLabelI18n(raw: string | null | undefined, t: TFunction): string {
  if (!raw) return t('competitiveAnalysis:component.unknown')
  const key = raw.toUpperCase()
  const map: Record<string, string> = {
    PENDING: t('status:waiting'),
    RUNNING: t('status:running'),
    SUCCEEDED: t('status:succeeded'),
    FAILED: t('status:failed'),
    SKIPPED: t('competitiveAnalysis:component.skipped'),
    succeeded: t('status:succeeded'),
    failed: t('status:failed'),
    pending: t('status:waiting'),
    running: t('status:running'),
  }
  return map[key] || map[raw] || t('competitiveAnalysis:component.processing')
}

export function buildMediaOverviewFactsI18n(detail: ReferenceContentDetail, t: TFunction): MediaOverviewFacts {
  const facts = buildMediaOverviewFacts(detail)
  const media = detail.media_analysis
  const contentType = media?.content_type || detail.content_type

  let mediaStatusLabel: string | null = null
  if (!media) mediaStatusLabel = t('competitiveAnalysis:mediaStatus.waiting')
  else if (media.status === 'PENDING' || media.status === 'RUNNING') mediaStatusLabel = t('competitiveAnalysis:mediaStatus.analyzing')
  else if (media.status === 'PARTIAL') mediaStatusLabel = t('competitiveAnalysis:mediaStatus.partial')
  else if (media.status === 'READY') mediaStatusLabel = t('competitiveAnalysis:mediaStatus.ready')
  else if (media.status === 'FAILED') mediaStatusLabel = t('competitiveAnalysis:mediaStatus.failed')

  const componentStatus = media?.component_status || {}
  const transcriptRaw = componentStatus.asr || componentStatus.transcript || null

  return {
    ...facts,
    contentTypeLabel:
      contentType === 'VIDEO' || contentType === 'IMAGE_POST' || contentType === 'MIXED'
        ? contentTypeLabel(contentType, t)
        : contentType,
    transcriptStatusLabel: transcriptRaw ? componentStatusLabelI18n(transcriptRaw, t) : media ? t('competitiveAnalysis:mediaStatus.none') : null,
    mediaStatusLabel,
  }
}

export function aiPendingCopyI18n(detail: ReferenceContentDetail, t: TFunction): string {
  const analysis = detail.analysis
  const recovery = detail.recovery
  const progress = detail.analysis_progress
  if (analysis?.status === 'failed') {
    const localized = tenantErrorMessageI18n(analysis.error_code, t)
    return localized || t('competitiveAnalysis:aiPending.failedDefault')
  }
  if (analysis?.status === 'running' || analysis?.status === 'pending') {
    return t('competitiveAnalysis:aiPending.running')
  }
  if (progress?.ai_failed || recovery?.execution_state === 'FAILED') {
    return t('competitiveAnalysis:aiPending.failedDefault')
  }
  if (recovery?.active_task_in_flight) {
    if (recovery.execution_state === 'QUEUED') {
      return t('competitiveAnalysis:recovery.queuedAi')
    }
    return t('competitiveAnalysis:aiPending.running')
  }
  if (isMediaLayerVisible(detail.media_analysis) && !analysis) {
    return t('competitiveAnalysis:aiPending.waitingAfterMedia')
  }
  if (detail.status === 'analyzing') {
    return t('competitiveAnalysis:aiPending.analyzing')
  }
  return t('competitiveAnalysis:aiPending.default')
}

export function capturePendingTextI18n(detail: ReferenceContentDetail, t: TFunction): string | null {
  const capture = detail.capture
  if (!capture || capture.status === 'pending' || capture.status === 'running') {
    return t('competitiveAnalysis:pending.fetchingContent')
  }
  if (capture.status === 'failed' && !isMediaLayerVisible(detail.media_analysis)) {
    const localized = tenantErrorMessageI18n(capture.error_code, t)
    return localized || t('competitiveAnalysis:pending.captureFailed')
  }
  return null
}

export function mediaAnalysisPendingTextI18n(detail: ReferenceContentDetail, t: TFunction): string | null {
  const media = detail.media_analysis
  if (!media || media.status === 'PENDING' || media.status === 'RUNNING') {
    return t('competitiveAnalysis:pending.mediaAnalyzing')
  }
  if (media.status === 'FAILED') {
    const localized = tenantErrorMessageI18n(media.error_code, t)
    return localized || t('competitiveAnalysis:pending.mediaFailed')
  }
  return null
}

export function aggregateStatusDetailI18n(detail: ReferenceContentDetail, t: TFunction): string | null {
  if (detail.status === 'failed') {
    const capture = detail.capture
    if (capture?.status === 'failed' && !isMediaLayerVisible(detail.media_analysis)) {
      return tenantErrorMessageI18n(capture.error_code, t) || t('competitiveAnalysis:pending.captureFailed')
    }
    const media = detail.media_analysis
    if (media?.status === 'FAILED') {
      return tenantErrorMessageI18n(media.error_code, t) || t('competitiveAnalysis:pending.mediaFailed')
    }
  }
  if (detail.status === 'partial' && detail.analysis?.status === 'failed') {
    return tenantErrorMessageI18n(detail.analysis.error_code, t) || t('competitiveAnalysis:aiPending.failedDefault')
  }
  return null
}

export function buildInsightSectionsI18n(analysis: AnalysisSnapshotOut, t: TFunction) {
  return buildInsightSections(analysis).map((section) => ({
    ...section,
    title: t(`competitiveAnalysis:insights.sectionTitles.${section.titleKey}`),
  }))
}
